<template lang="html">
  <div class="cw-login">
    <!-- reCAPTCHA container (invisible) -->

    <div class="hero is-small has-text-centered is-bottom-paddingless">
      <div class="hero-body">
        <div class="has-text-grey-medium">
          <icon name="profile" scale="4" label="set"></icon>
        </div>
        <h1 class="title is-size-4">
          {{ showMFAPrompt ? 'Two-Factor Authentication' : 'Login' }}
        </h1>
        <h3 class="subtitle is-size-5">
          {{
            showMFAPrompt
              ? 'Enter the verification code sent to your phone.'
              : 'You must be logged in to use the App.'
          }}
        </h3>
      </div>
    </div>
    <section class="section is-top-paddingless">
      <!-- Regular Login Form -->
      <form v-if="!showMFAPrompt" @submit="login" class="form">
        <div class="field">
          <div class="control">
            <input
              class="input is-underlined is-medium"
              type="text"
              placeholder="Email"
              v-model="username"
            />
          </div>
        </div>
        <div class="field">
          <div class="control">
            <input
              class="input is-underlined is-medium"
              type="password"
              placeholder="Password"
              v-model="password"
            />
          </div>
        </div>
        <div class="field is-grouped is-grouped-right">
          <div class="control">
            <button
              class="button is-medium is-black is-outlined is-rounded"
              type="button"
              name="button"
              @click="$modal.show('RequestPasswordReset')"
            >
              Forgot Password?
            </button>
          </div>
          <div class="control">
            <button
              class="button is-medium is-primary is-outlined is-rounded"
              type="submit"
              name="button"
            >
              Log in
            </button>
          </div>
        </div>
      </form>

      <!-- MFA Verification Form -->
      <div v-else>
        <cw-mfa-verification
          v-model="mfaCode"
          title="Two-Factor Authentication"
          message="Code sent to"
          :phoneNumber="userPhoneNumber"
          :errorMessage="mfaErrorMessage"
          submitText="Verify & Login"
          cancelText="Cancel"
          @verify="verifyMFACode"
          @resend="resendCode"
          @cancel="cancelMFA"
        />
      </div>
    </section>
    <section class="section has-text-centered">
      <h3 class="title is-size-6">Don't have an account?</h3>
      <router-link
        to="/register"
        class="button is-medium is-black is-outlined is-rounded"
        >Register</router-link
      >
    </section>
  </div>
</template>

<script>
import MFAVerification from '@/components/cwkit/MFAVerification';

export default {
  name: 'cw-login',
  // share common functionality with component mixins
  mixins: [],
  props: {},
  // variables
  data() {
    return {
      username: '',
      password: '',
      showMFAPrompt: false,
      mfaCode: '',
      mfaError: false,
      mfaErrorMessage: '',
      resending: false,
      userPhoneNumber: null,
    };
  },
  computed: {},
  components: {
    'cw-mfa-verification': MFAVerification,
  },
  // methods
  watch: {},
  methods: {
    login(e) {
      if (e) e.preventDefault();
      let username = this.username;
      let password = this.password;
      this.$modal.show('wifi-loader', { message: 'Processing...' });

      this.$store
        .dispatch('user/authenticate', { username, password })
        .then(result => {
          this.$modal.hide('wifi-loader');

          console.log('login result', result);
          // Check if MFA is required
          if (result && result.requiresMFA) {
            this.showMFAPrompt = true;
            // Store phone number from MFA hints for display
            if (result.hints && result.hints.length > 0) {
              // hints[0].phoneNumber contains the phone number
              this.userPhoneNumber = result.hints[0].phoneNumber;
            }
            // Only send SMS verification code if not already sent (mobile sends during signInWithMfa)
            if (!result.codeSent) {
              this.$store
                .dispatch('user/sendMfaVerificationCode')
                .catch(error => {
                  window.console.error('Failed to send MFA code:', error);
                  this.$modal.show('alert', {
                    title: 'Error',
                    message:
                      'Failed to send verification code. Please try again.',
                    buttons: {
                      closeTitle: 'Ok',
                    },
                    type: 'warning',
                  });
                });
            }
            return;
          }

          if (this.$store.state.user.authenticated) {
            this.$modal.show('alert', { title: 'Successfully logged in' });
            setTimeout(() => {
              this.$modal.hide('alert');
            }, 800);
            let pendingRoute = this.$store.state.pendingRoute || '/';
            this.$router.push({ path: pendingRoute });
          } else {
            this.$modal.show('alert', {
              title: 'Login Error',
              message: 'Invalid login credentials.',
              buttons: {
                closeTitle: 'Ok',
              },
              type: 'warning',
            });
          }
        });
    },
    async verifyMFACode(code) {
      // Handle both event object (from form submit) and direct code parameter (from component)
      const verificationCode = typeof code === 'string' ? code : this.mfaCode;

      if (verificationCode.length !== 6) return;

      this.$modal.show('wifi-loader', { message: 'Verifying...' });
      this.mfaError = false;
      this.mfaErrorMessage = '';

      try {
        await this.$store.dispatch('user/verifyMfaCode', {
          code: verificationCode,
        });

        this.$modal.hide('wifi-loader');
        this.$modal.show('alert', { title: 'Successfully logged in' });
        setTimeout(() => {
          this.$modal.hide('alert');
        }, 800);

        let pendingRoute = this.$store.state.pendingRoute || '/';
        this.$router.push({ path: pendingRoute });
      } catch (error) {
        this.$modal.hide('wifi-loader');
        window.console.error('MFA verification error:', error);

        // Set error state
        this.mfaError = true;
        this.mfaErrorMessage = 'Invalid code. Please try again.';
        this.mfaCode = '';
      }
    },
    async resendCode() {
      this.resending = true;
      this.mfaError = false;
      this.mfaErrorMessage = '';
      this.mfaCode = '';

      try {
        // Use platform-appropriate resend method
        const mfaResolver = this.$store.state.user.mfaResolver;
        if (mfaResolver === 'native') {
          // Mobile: Use native resend
          await this.$store.dispatch('user/resendMfaCode');
        } else {
          // Web: Use standard sendMfaVerificationCode
          await this.$store.dispatch('user/sendMfaVerificationCode');
        }

        this.$modal.show('alert', {
          title: 'Code Resent',
          message:
            'A new verification code has been sent to your registered phone number.',
          buttons: {
            closeTitle: 'Ok',
          },
        });
      } catch (error) {
        window.console.error('Failed to resend MFA code:', error);

        this.$modal.show('alert', {
          title: 'Error',
          message: 'Failed to resend verification code. Please try again.',
          buttons: {
            closeTitle: 'Ok',
          },
          type: 'warning',
        });
      } finally {
        this.resending = false;
      }
    },
    cancelMFA() {
      this.showMFAPrompt = false;
      this.mfaCode = '';
      this.mfaError = false;
      this.mfaErrorMessage = '';
      this.userPhoneNumber = null;
      this.$store.commit('user/clearMfaState');
      // Clean up reCAPTCHA when canceling
    },
  },
  mounted() {
    let email = this.$route.query.email;
    this.username = email;
  },
};
</script>

<style lang="" scoped></style>
